/* eslint-env browser */
// Ethiopic web fonts for the portal, self-hosted (web-app/src/theme/fonts/,
// Noto Sans Ethiopic 2.102 subsets, OFL 1.1 - see fonts/OFL.txt).
//
// Each weight is declared twice:
// - as "Noto Sans Ethiopic", for the theme's font stack and print templates;
// - as "Roboto" limited to the Ethiopic code points (unicode-range). Roboto's
//   Latin faces come from Google Fonts (public/index.html), and the browser
//   joins faces of one family by unicode-range, so every existing inline
//   fontFamily '"Roboto", "Helvetica", "Arial", sans-serif' also draws
//   Amharic and Tigrinya with Noto Sans Ethiopic instead of whatever the
//   computer happens to have.
// A browser downloads a file only when the page shows a character it covers.

import eth400 from './fonts/noto-sans-ethiopic-400.woff2';
import eth500 from './fonts/noto-sans-ethiopic-500.woff2';
import eth700 from './fonts/noto-sans-ethiopic-700.woff2';
import ethExt400 from './fonts/noto-sans-ethiopic-ext-400.woff2';
import ethExt500 from './fonts/noto-sans-ethiopic-ext-500.woff2';
import ethExt700 from './fonts/noto-sans-ethiopic-ext-700.woff2';

export const ETHIOPIC_RANGE = 'U+1200-139F, U+25CC';
export const ETHIOPIC_EXT_RANGE = 'U+2D80-2DDF, U+AB00-AB2F, U+1E7E0-1E7FF, U+25CC';

// The portal's font stack: Roboto (Latin from Google Fonts, Ethiopic from the
// faces below), then the explicit Ethiopic family, then system fonts.
export const FONT_STACK = '"Roboto", "Noto Sans Ethiopic", system-ui, -apple-system, "Segoe UI", Arial, sans-serif';

const FACES = [
  { weight: 400, core: eth400, ext: ethExt400 },
  { weight: 500, core: eth500, ext: ethExt500 },
  { weight: 700, core: eth700, ext: ethExt700 },
];

const absolute = (url) => {
  try {
    return new URL(url, window.location.href).href;
  } catch (e) {
    return url;
  }
};

const face = (family, url, weight, range) =>
  `@font-face{font-family:"${family}";font-style:normal;font-weight:${weight};font-display:swap;` +
  `src:url("${url}") format("woff2");unicode-range:${range};}`;

// CSS text with every @font-face rule. absoluteUrls: true for documents that
// do not share the page's base URL (print iframes, html2pdf, new windows).
export const ethiopicFontFaceCss = ({ absoluteUrls = false, families = ['Noto Sans Ethiopic', 'Roboto'] } = {}) => {
  const fix = absoluteUrls ? absolute : (u) => u;
  const rules = [];
  families.forEach((family) => {
    FACES.forEach(({ weight, core, ext }) => {
      rules.push(face(family, fix(core), weight, ETHIOPIC_RANGE));
      rules.push(face(family, fix(ext), weight, ETHIOPIC_EXT_RANGE));
    });
  });
  return rules.join('\n');
};

// Resolves once the Ethiopic faces in use are downloaded, so a canvas-based
// export (html2pdf / html2canvas) or a print snapshot does not catch the
// fallback font. Never rejects; gives up after timeoutMs.
export const ensureEthiopicFonts = (weights = [400, 700], timeoutMs = 4000, doc = document) => {
  try {
    if (!doc || !doc.fonts || typeof doc.fonts.load !== 'function') return Promise.resolve(false);
    const loads = weights.map((w) => doc.fonts.load(`${w} 16px "Noto Sans Ethiopic"`, 'ሀ'));
    const timer = new Promise((resolve) => setTimeout(() => resolve(false), timeoutMs));
    return Promise.race([Promise.all(loads).then(() => true, () => false), timer]);
  } catch (e) {
    return Promise.resolve(false);
  }
};
